import { SimpleFormButtonsProps } from './SimpleForm.types';
import { Button, Grid, Stack } from '@mui/material';
import { SubmitButton } from 'components/buttons';
import ButtonWithConfirmationDialog from 'components/buttons/ButtonWithConfirmationDialog';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const SimpleFormButtons = <T extends FieldValues>({
  formState,
  onDelete,
  onCancel,
  deleteButtonText,
  isFormEditable,
  deleteConfirmationText,
  isLoading = false,
  spacing = 3,
  dialogProps,
}: SimpleFormButtonsProps<T>) => {
  const { t } = useTranslation();
  const { isDirty, isValid, isSubmitSuccessful } = formState;
  const showCancelConfirmation = isDirty && !isSubmitSuccessful;

  const renderCancelButton = () => {
    if (!onCancel) {
      return null;
    }

    return showCancelConfirmation ? (
      <ButtonWithConfirmationDialog
        handleConfirm={onCancel}
        buttonText={t('components:form.cancel')}
        title={t('interactions.areYouSure')}
      >
        {t('components:form.goBackConfirmationText')}
      </ButtonWithConfirmationDialog>
    ) : (
      <Button color="tertiary" onClick={onCancel}>
        {t('components:form.cancel')}
      </Button>
    );
  };

  const renderRemoveButton = () => {
    if (!onDelete) {
      return null;
    }

    return (
      <ButtonWithConfirmationDialog
        title={deleteButtonText || t('components:form.remove')}
        handleConfirm={onDelete}
        buttonText={deleteButtonText || t('components:form.remove')}
        variant="text"
        buttonProps={{ color: 'error', disabled: !isFormEditable }}
        dialogProps={dialogProps}
      >
        {deleteConfirmationText}
      </ButtonWithConfirmationDialog>
    );
  };

  const renderSubmitButton = () => (
    <SubmitButton isSubmitting={isLoading} disabled={!isValid || !isDirty || !isFormEditable} />
  );

  const removeButton = renderRemoveButton();
  const submitButton = renderSubmitButton();
  const cancelButton = renderCancelButton();

  return (
    <Grid container item xs={12} alignItems="center" justifyContent="space-between">
      <Grid item>{removeButton}</Grid>
      <Grid item>
        <Stack direction="row" spacing={spacing} alignItems="center">
          {cancelButton}
          {submitButton}
        </Stack>
      </Grid>
    </Grid>
  );
};
